import React, { useState, useEffect, useContext } from "react";
import { ThemeContext, PatientContext } from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";

import { useParams, useHistory, useLocation } from "react-router-dom";

import { Row } from "react-bootstrap";
import { Line } from "react-chartjs-2";
import { parse } from "date-fns";

import theme from "../../index.scss";
import ComponentWrapper from "../Dashboard/ComponentWrapper";
import MedicalDetailsChartOptions from "../Dashboard/MedicalDetailsChartOptions";
import ModalPdf from "../Dashboard/ModalPdf";
import TriggerChart from "../Dashboard/TriggerChart";
import SubmitButton from "../UIComponents/SubmitButton";

import fillInDays from "../../lib/fillInDates";
import getDaysAgo from "../../lib/getDaysAgo";
import triggerWithSetVal from "../../lib/triggerWithSetVal";

import { getAdditionalNote } from "../../api/TBIRequests";

const CHART_TYPES = {
  LINE: "line",
  BAR: "bar",
};

const MedicalDetailsChart = ({ error, topSymptoms, data, retry, user }) => {
  const [type, setType] = useState(CHART_TYPES.LINE);
  const [additionalNotes, setAdditionalNotes] = useState([]);
  const [showLineModal, setShowLineModal] = useState(false);
  const params = useParams();
  const { dark } = useContext(ThemeContext);
  const { patient } = useContext(PatientContext);
  const { t } = useTranslation();

  const tSymptom = (factor) => {
    const categories = ["cognitive","sleep","emotional","physical","speech","vision"];
    const safe = (factor || "").trim().toLowerCase()
      .replace(/ /g,"_").replace(/\//g,"_").replace(/,/g,"")
      .replace(/:/g,"").replace(/'/g,"").replace(/[()]/g,"").replace(/-/g,"_")
      .substring(0, 60);
    for (const cat of categories) {
      const key = `symptom.${cat}.${safe}`;
      const translated = t(key);
      if (translated !== key) return translated;
    }
    return factor;
  };

  const history = useHistory();
  const location = useLocation();
  // Access the parameters from location.state if available,
  // otherwise provide default values
  const medicalType = location.state?.medicalType || "Vision";
  const fromMedicalDetailsChart =
    location.state?.fromMedicalDetailsChart || false;
  const days = location.state?.days || 7;

  const filteredData = data
    ?.filter((obj) => obj.category !== "SDOH")
    .filter(
      (obj) =>
        parse(obj.symptom_date.slice(0, 10), "yyyy-MM-dd", new Date()) >
        getDaysAgo(days)
    );

  const trackedSymptoms = [];
  const factorSet = new Set();

  filteredData.forEach((obj) => {
    if (!factorSet.has(obj.factor)) {
      factorSet.add(obj.factor);
      trackedSymptoms.push({
        factor: obj.factor,
        category: obj.category,
        subcategory: obj.subcategory,
      });
    }
  });

  const selectedSymptom = decodeURIComponent(params.symptom);

  const colors = [
    theme.chart1,
    theme.chart2,
    theme.chart3,
    theme.chart4,
    theme.chart5,
    theme.chart6,
    theme.chart7,
    theme.chart8,
    theme.chart9,
    theme.chart10,
    theme.chart1,
    theme.chart2,
    theme.chart3,
    theme.chart4,
    theme.chart5,
    theme.chart6,
    theme.chart7,
    theme.chart8,
    theme.chart9,
    theme.chart10,
    theme.chart1,
    theme.chart2,
    theme.chart3,
    theme.chart4,
    theme.chart5,
    theme.chart6,
    theme.chart7,
    theme.chart8,
    theme.chart9,
    theme.chart10,
  ];

  const handleLineModalClose = () => setShowLineModal(false);
  const handleLineModalShow = () => setShowLineModal(true);
  useEffect(() => {
    getAdditionalNote({
      patientId: patient?.patientId,
      therapistId: user.userType === "Therapist" ? user.userId : null,
    })
      .then(({ data }) => {
        setAdditionalNotes(data);
      })
      .catch((e) => console.log("getAdditionalNotesError", e));
  }, []);

  let datasetArr = [];
  trackedSymptoms.forEach((symptom) => {
    let dataset = data.filter(({ factor }) => factor === symptom.factor);

    dataset = dataset.filter(
      ({ subcategory }) => subcategory === medicalType.toLowerCase()
    );
    datasetArr.push(dataset);
  });

  let triggerList = triggerWithSetVal(data);
  datasetArr.push(triggerList);
  const filterDataByAssignedDays = (dataset) => {
    const datasetConvertedToChartData = dataset.map((item) => {
      return {
        // CURRENT BUG, Must remove time stamp or labels do not work correctly
        x: item.symptom_date.slice(0, 10),
        y: item.severity,
        desc: item.description,
      };
    });
    const filteredByAssignedDays = datasetConvertedToChartData.filter(
      (item) => parse(item.x, "yyyy-MM-dd", new Date()) > getDaysAgo(days)
    );
    return filteredByAssignedDays;
  };

  const newData = {
    datasets: [],
  };
  let { datasets } = newData;

  datasetArr.forEach((dataset, index) => {
    if (dataset.length != 0 && index < datasetArr.length - 1) {
      datasets.push({
        hidden: false,
        fill: type !== "line",
        label: tSymptom(trackedSymptoms[index].factor),
        backgroundColor: colors[index],
        borderColor: colors[index],
        borderWidth: 1,
        hoverBackgroundColor: colors[index],
        hoverBorderColor: "white",
        data: filterDataByAssignedDays(dataset),
      });
    } else if (dataset.length != 0) {
      datasets.push({
        label: "SDOH",
        data: filterDataByAssignedDays(dataset),
        pointStyle: "triangle",
        type: "scatter",
        backgroundColor: theme.primary,
        borderWidth: 1,
      });
    }
  });

  const ChartDataset = fillInDays(datasets, days);
  newData.datasets = ChartDataset.slice(0, ChartDataset.length - 1);
  newData.notes = additionalNotes;

  const triggerOptions = {
    animation: {
      duration: 1,
      onComplete: function () {
        // adding trigger count for each day if any. It will be showing on top of the most highest point.
        var chartInstance = this.chart,
          ctx = chartInstance.ctx;

        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.fillStyle = theme.primary;
        const triggerIndex = ChartDataset.length - 1;

        // loop through each dataset again to calculate the change in severity score
        // data will be used to display a red cross if there is a 10% increase
        const severityChangeData = [];
        const symptomDataLength = this.data.datasets[0].data.length;
        let lastVal = null;
        for (let i = 0; i < symptomDataLength; i++) {
          let curVal = this.data.datasets[0].data[i]?.y?.y;

          if (curVal == null) {
            severityChangeData.push(null);
            continue;
          }

          if (lastVal == null) {
            severityChangeData.push(null);
            lastVal = curVal;
            continue;
          }

          const change = curVal - lastVal;
          severityChangeData.push(change);
          lastVal = curVal;
        }

        const meta = chartInstance.controller.getDatasetMeta(1);
        meta.data.forEach(function (bar, index) {
          var data = ChartDataset[triggerIndex].data[index].y;
          if (data) {
            let triggers = data?.desc.split(",");
            ctx.fillText(`${triggers.length}`, bar._model.x, bar._model.y - 5);
          }
        });

        const meta2 = chartInstance.controller.getDatasetMeta(0);
        meta2.data.forEach(function (bar, index) {
          if (data) {
            if (
              severityChangeData[index] !== null &&
              severityChangeData[index] >= 10
            ) {
              ctx.fillText(`❌`, bar._model.x, bar._model.y + 7);
            }
          }
        });
        ctx.fillText(`❌ Above 10% change`, 600, 57);
      },
    },
  };

  const colorOverRide = {
    scales: {
      xAxes: [
        {
          type: "time",
          time: {
            unit: "day",
            tooltipFormat: "dddd",
          },
          ticks: {
            fontColor: dark ? theme.darkModeText : theme.black,
          },
        },
      ],
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
            fontColor: dark ? theme.darkModeText : theme.black,
          },
          scaleLabel: {
            display: true,
            labelString: t("medicaldetailschart.intensity_level"),
            fontColor: dark ? theme.darkModeText : theme.black,
          },
        },
      ],
    },
  };

  const extendedChartOption = {
    maintainAspectRatio: false,
    title: {
      display: true,
      text: `${medicalType} Report`,
      fontFamily: "quicksand",
      fontStyle: "bold",
      fontColor: dark ? theme.darkModeText : "#212121",
    },
    legend: {
      labels: {
        fontColor: dark ? theme.darkModeText : "#212121",
        usePointStyle: true,
        boxWidth: 8,
      },
    },
  };

  const classNameArr = () => {
    const totalNum = newData?.datasets?.length;
    const arr = [];
    for (let i = 0; i < totalNum; i++) {
      arr.push(`${medicalType} ${days} Report${i}`);
    }
    return arr;
  };

  if (type === CHART_TYPES.LINE) {
    return (
      <>
        {error && (
          <ComponentWrapper>
            {t("charts.error.0")}
            <SubmitButton onClick={retry}>
              {t("charts.error.1")}
            </SubmitButton>
          </ComponentWrapper>
        )}
        <ComponentWrapper>
          <Row className="d-flex justify-content-around">
            <SubmitButton
              onClick={() => {
                if (dark) {
                  alert(t("common.off_dark_mode_export"));
                } else {
                  handleLineModalShow();
                }
              }}
            >
              {t("common.export_pdf")}
            </SubmitButton>
          </Row>
        </ComponentWrapper>
        <ModalPdf
          show={showLineModal}
          handleClose={handleLineModalClose}
          chart={classNameArr()}
          isReport={true}
          data={data}
          additionalNotes={additionalNotes}
          days={days}
          topSymptoms={topSymptoms}
        />

        {newData?.datasets?.map((element, index) => {
          const newData2 = {
            datasets: [],
          };
          const arr2 = new Array();
          arr2.push(element);
          const triggerIndex = ChartDataset.length - 1;
          const trigger = ChartDataset[triggerIndex];

          {
            /* trigger bug, want to use serverity as height*/
          }
          // const arr1 = trigger.data;
          // for(let i = 0; i < arr1.length; i++){
          //     let date = arr1[i]?.y?.x;
          //     let high = -1;
          //     for(let j = 0; j < element.data.length; j++){
          //         if(element.data[j].y?.x == date){
          //             high = element.data[j]?.y?.y;
          //         }
          //     }
          //     if(arr1[i].y){
          //         arr1[i].y.y = high;
          //     }
          // }

          arr2.push(trigger);
          newData2.datasets = arr2;
          newData2.notes = additionalNotes;
          return (
            <div
              className={`${medicalType} ${days} Report${index}`}
              key={index}
            >
              <div style={{ display: "flex" }}>
                <div style={{ flex: "2" }}>
                  <ComponentWrapper key={element.label}>
                    <Line
                      data={newData2}
                      // width={100}
                      height={300}
                      options={{
                        ...triggerOptions,
                        ...MedicalDetailsChartOptions,
                        ...extendedChartOption,
                        ...colorOverRide,
                      }}
                    />
                  </ComponentWrapper>
                </div>

                <div style={{ flex: "1" }}>
                  <ComponentWrapper
                    key={element.label}
                    style={{ flexGrow: "1" }}
                  >
                    <TriggerChart data={data} height={268} stacked={true} />
                  </ComponentWrapper>
                </div>
              </div>
            </div>
          );
        })}

        <SubmitButton
          onClick={
            !fromMedicalDetailsChart
              ? () => history.push("/")
              : () =>
                  history.push({ pathname: "/charts", state: { days: days } })
          }
        >
          {t("common.go_back")}
        </SubmitButton>
      </>
    );
  } else if (type === CHART_TYPES.BAR) {
    return (
      <>
        {error && (
          <ComponentWrapper>
            {t("charts.error.0")}
            <SubmitButton onClick={retry}>
              {t("charts.error.1")}
            </SubmitButton>
          </ComponentWrapper>
        )}
        <ComponentWrapper>
          <Row className="d-flex justify-content-around">
            <SubmitButton onClick={() => setType("line")}>
              {t("medicaldetailschart.change_to_line")}
            </SubmitButton>
            <SubmitButton
              onClick={() => {
                if (dark) {
                  alert(t("common.off_dark_mode_export"));
                }
              }}
            >
              {t("common.export_pdf")}
            </SubmitButton>
          </Row>
        </ComponentWrapper>
        <SubmitButton onClick={() => history.push("/")}>
          {t("common.go_back")}
        </SubmitButton>
      </>
    );
  }
};

export default MedicalDetailsChart;